import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Search as SearchIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/looseend/EmptyState";
import { HostedLoopsNotice } from "@/components/looseend/HostedLoopsNotice";
import { LoopStatusBadge } from "@/components/looseend/LoopStatusBadge";
import { useInspector } from "@/components/looseend/inspector-context";
import { DEFAULT_FILTERS, RESULT_TYPE_LABEL, searchLoops } from "@/lib/looseend/search";
import { formatDate } from "@/lib/looseend/format";
import { ACTIVE_STATUSES, STATUS_META } from "@/lib/looseend/status";
import { useHostedLoops } from "@/lib/loops/product/hostedLoops";
import type { LoopStatus, SearchFilters } from "@/lib/looseend/types";
import { analytics } from "@/lib/analytics";

export const Route = createFileRoute("/app/search")({
  head: () => ({
    meta: [
      { title: "Search — L∞seEnd" },
      {
        name: "description",
        content: "Search your Loops and the evidence attached to them.",
      },
      { property: "og:title", content: "Search — L∞seEnd" },
      { property: "og:description", content: "Find any Loop or piece of evidence." },
    ],
  }),
  component: SearchPage,
});

/**
 * WP-PT — Search reads one authoritative source: the hosted Loop list (and the
 * evidence it carries). Nothing browser-local participates, and nothing is
 * recorded about the query.
 */
function SearchPage() {
  const { open } = useInspector();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<LoopStatus | "all">("all");
  const [range, setRange] = useState<SearchFilters["range"]>("all");

  const hosted = useHostedLoops();

  const results = useMemo(
    () => searchLoops(hosted.loops, query, { ...DEFAULT_FILTERS, status, range }),
    [hosted.loops, query, status, range],
  );

  useEffect(() => {
    analytics.track("search_opened", { interface_surface: "search_page" });
  }, []);

  return (
    <div className="h-full overflow-y-auto px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Search</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Find something LooseEnd is tracking.</p>

        <form
          className="mt-5 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="relative min-w-0 flex-1">
            <SearchIcon
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="A person, a bill, an event, an issue…"
              aria-label="Search query"
              className="pl-9"
            />
          </div>
        </form>

        <div className="mt-3 flex flex-wrap gap-2">
          <Select value={status} onValueChange={(v) => setStatus(v as LoopStatus | "all")}>
            <SelectTrigger className="w-[180px]" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              {ACTIVE_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_META[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={(v) => setRange(v as SearchFilters["range"])}>
            <SelectTrigger className="w-[170px]" aria-label="Filter by date range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {query.trim() ? (
          <HostedLoopsNotice
            phase={hosted.phase}
            message={hosted.message}
            onRetry={hosted.refresh}
            rows={2}
          />
        ) : null}

        {query.trim() && hosted.phase === "ready" ? (
          results.length ? (
            <ul className="mt-6 space-y-2">
              {results.map((result) => (
                <li key={result.id}>
                  <button
                    type="button"
                    disabled={!result.loopId}
                    onClick={() => result.loopId && open(result.loopId)}
                    className="w-full rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-border-strong disabled:cursor-default"
                  >
                    <p className="text-sm font-semibold text-foreground">{result.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {result.excerpt}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-surface px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {RESULT_TYPE_LABEL[result.type]}
                      </span>
                      {result.status ? <LoopStatusBadge status={result.status} size="sm" /> : null}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {formatDate(result.date)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              className="mt-6"
              icon={<SearchIcon className="size-6" aria-hidden="true" />}
              title="No matches."
              description="Try describing the person, bill, event, or issue you’re looking for."
            />
          )
        ) : null}
      </div>
    </div>
  );
}
